import { HttpClient } from '@angular/common/http';
import { Injectable, inject, signal } from '@angular/core';
import { SafeResourceUrl } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { environment } from '@environment';
import { FileUpload, Screenshot, UploadObject } from '@models';
import {
  DataService,
  DebugDataService,
  LoggingService,
  MlTrackingService,
  SimulationService,
  SnackbarService,
} from '@services';
import { SceneStore } from '@stores';
import * as CryptoJS from 'crypto-js';
import { EMPTY, catchError, forkJoin, retry, tap, throwError } from 'rxjs';
import { v4 as uuidv4 } from 'uuid';

@Injectable({
  providedIn: 'root',
})
export class UploadService {
  private dataService = inject(DataService);
  private debugDataService = inject(DebugDataService);
  private http = inject(HttpClient);
  private loggingService = inject(LoggingService);
  private mlTrackingService = inject(MlTrackingService);
  private router = inject(Router);
  private sceneStore = inject(SceneStore);
  private simulationService = inject(SimulationService);
  private snackbarService = inject(SnackbarService);

  baseUrl = environment.API_HOST;
  activeParams: string;
  videoChunks: Blob[] = [];
  blockIds: string[] = [];
  isUploadInProgress = signal(false);
  fileUploadProgress = signal(0);
  chunkCount = 0;
  selectedReferenceImage: Screenshot;
  screenshots: SafeResourceUrl[] = [];
  uploadObject: UploadObject;

  createData(notes: string, selfAssessmentRating: number) {
    if (!this.dataService.context) {
      this.snackbarService
        .showSnackBar({ messageKey: 'userContext.missing' })
        .subscribe();
    }
    const screenshots = [
      this.sceneStore.finalScreenShot(),
      ...this.sceneStore.screenshots(),
    ].map((screenshot) => {
      const id = uuidv4();
      this.sceneStore.screenshotsUpload().push({
        id,
        blob: screenshot,
        mimeType: 'image/png',
      });
      return {
        groupLanguageKey: '',
        descriptionLanguageKey: 'screenshot.description' + id,
        nameLanguageKey: 'screenshot.name' + id,
        sortId: 0,
        id: id,
        fileName: 'Screenshot.png',
        fileGeneratedName: '',
      };
    });
    this.uploadObject = {
      id: '9f3b8a28-7e6b-4a5f-bc90-7e9c0f99e4cf',
      actualSceneIdentifier: this.sceneStore.data().identifier,
      sceneIdentifier: this.sceneStore.data().identifier,
      sceneNameLanguageKey: this.sceneStore.data().nameLanguageKey,
      sceneSectionLanguageKey:
        this.sceneStore.data().sceneSectionNameLanguageKey,
      actualSceneNameLanguageKey: this.sceneStore.data().nameLanguageKey,
      courseEntry: this.dataService.courseEntry
        ? {
            courseEntryId: this.dataService.courseEntry,
            ensureCertificateIfAchieved: true,
          }
        : null,
      startDate:
        this.simulationService.recordingStartTime ??
        new Date(new Date().toUTCString()).toISOString(),
      endDate:
        this.simulationService.recordingEndTime ??
        new Date(new Date().toUTCString()).toISOString(),
      state: 10,
      userSession: uuidv4(),
      simulatorIdentifier: environment.simulatorIdentifier,
      simulatorVersion: environment.version,
      userInterfaceVersion: 'N/A',
      score: this.sceneStore
        .reportItems()
        .reduce((acc, item) => item.score + acc, 0),
      maxScore: this.sceneStore
        .reportItems()
        .reduce((acc, item) => item.maxScore + acc, 0),
      selfAssessmentRating,
      reportSections: [
        {
          sortId: 0,
          isVisible: true,
          nameLanguageKey: this.sceneStore.data().reportSections
            ? this.sceneStore.data().reportSections[0].nameLanguageKey
            : '',
          sectionIdentifier: this.sceneStore.data().reportSections
            ? this.sceneStore.data().reportSections[0].identifier
            : '',
          score: this.sceneStore
            .reportItems()
            .reduce((acc, item) => item.score + acc, 0),
          maxScore: this.sceneStore
            .reportItems()
            .reduce((acc, item) => item.maxScore + acc, 0),
          reportItems: this.sceneStore.reportItems(),
        },
      ],
      screenshots,
      notes,
      movies: [
        {
          nameLanguageKey: 'name.key-' + uuidv4(),
          id: this.sceneStore.videoUpload().id,
          fileName: 'SceneMovie.mp4',
          fileGeneratedName: '',
        },
      ],
      reportAttachments: [
        {
          nameLanguageKey: 'name.key-' + uuidv4(),
          id: this.mlTrackingService.trackingDataUpload.id,
          fileName: 'TrackingData.zip',
          fileGeneratedName: '',
        },
        {
          nameLanguageKey: 'name.key-' + uuidv4(),
          id: this.debugDataService.debugDataUpload.id,
          fileName: 'DebugData.json',
          fileGeneratedName: '',
        },
      ],
    };
    this.loggingService.log('UPLOAD OBJECT');
    this.loggingService.log(this.uploadObject);
  }

  submitData() {
    if (this.dataService.context) {
      const randomNum = CryptoJS.lib.WordArray.random(16);
      const iv = randomNum.toString(CryptoJS.enc.Base64);
      const keyString = 'fxhToKHXW82SlxvdfrYsIXMak2RdmpLD';
      const key = CryptoJS.enc.Utf8.parse(keyString);
      const value = CryptoJS.enc.Utf8.parse(JSON.stringify(this.uploadObject));
      const encrypted = CryptoJS.AES.encrypt(value, key, {
        iv: randomNum,
        mode: CryptoJS.mode.CBC,
        padding: CryptoJS.pad.Pkcs7,
      }).toString();
      const encryptedUpload = encrypted.toString();

      return this.http
        .post<{ [key: string]: string }>(
          this.baseUrl + `user/reports/context/${this.dataService.context}`,
          encryptedUpload,
          {
            headers: {
              'Content-Type': 'application/json',
              apiKey: 'kpgwtlAF2WTawB3ZlcUUsmCIYZTL4E',
              iv,
            },
          },
        )
        .pipe(
          catchError((error) => {
            // If error is 409, do not retry
            if (error?.status === 409) {
              this.isUploadInProgress.set(false);
              return EMPTY;
            }
            this.isUploadInProgress.set(false);
            return throwError(() => error);
          }),
          retry(3),
        )
        .subscribe((response) => {
          this.sceneStore.screenshotsUpload().forEach((screenshot) => {
            this.uploadScreenshot(screenshot, response[screenshot.id]);
          });
          this.uploadBlobInChunks(
            this.sceneStore.videoUpload().blob,
            response[this.sceneStore.videoUpload().id],
          );
          this.uploadTrackingData(
            response[this.mlTrackingService.trackingDataUpload.id],
          );
          this.uploadDebugData(
            response[this.debugDataService.debugDataUpload.id],
          );
        });
    } else {
      this.snackbarService
        .showSnackBar({
          messageKey: 'userContext.missing',
        })
        .subscribe(() => {
          this.submitData();
        });
      return EMPTY;
    }
  }

  uploadScreenshot(file: FileUpload, url: string) {
    const headers = {
      'x-ms-blob-type': 'BlockBlob',
      'Content-Type': file.mimeType ?? 'image/png',
    };

    return this.http
      .put(url, file.blob, { headers })
      .pipe(
        catchError(() => {
          this.showError('upload.ErrorScreenshots', 'snackbar.retryAction');
          return EMPTY;
        }),
      )
      .subscribe(() => {
        this.chunkCount++;
        this.fileUploadProgress.set(
          100 *
            (this.chunkCount /
              (this.videoChunks.length +
                this.sceneStore.screenshotsUpload().length +
                1)),
        );
      });
  }

  uploadBlobInChunks(file: Blob, url: string) {
    const chunkSize = 1024 * 1024;
    const totalChunks = Math.ceil(file.size / chunkSize);
    const chunks: Blob[] = [];

    for (let offset = 0; offset < file.size; offset += chunkSize) {
      const chunk = file.slice(offset, offset + chunkSize);
      chunks.push(chunk);
    }

    forkJoin(
      chunks.map((chunk, index) => {
        const blockId = btoa(this.createId(10));
        this.blockIds.push(blockId);
        const blockUrl = `${url}&comp=block&blockid=${blockId}`;
        this.loggingService.log('Chunk: ' + index);
        return this.http.put(blockUrl, chunk, {
          headers: {
            'x-ms-blob-type': 'BlockBlob',
            'Content-Type': file.type,
          },
        });
      }),
    )
      .pipe(
        catchError((error) => {
          this.loggingService.log('ERROR: ' + error);
          return EMPTY;
        }),
        tap(() => {
          this.chunkCount++;
          this.fileUploadProgress.set(
            100 *
              (this.chunkCount /
                (totalChunks + this.sceneStore.screenshotsUpload().length + 1)),
          );
        }),
      )
      .pipe(
        catchError(() => {
          this.showError('upload.ErrorVideos', 'snackbar.retryAction');
          return EMPTY;
        }),
      )
      .subscribe(() => {
        let requestBody = `<?xml version="1.0" encoding="utf-8"?>
              <BlockList>`;
        this.blockIds.forEach((id) => {
          requestBody = requestBody + `<Latest>${id}</Latest>`;
        });
        requestBody = requestBody + '</BlockList>';

        this.http
          .put(url + `&comp=blocklist`, requestBody, {
            headers: {
              'Content-Type': 'text/plain; charset=UTF-8',
            },
          })
          .pipe(
            catchError(() => {
              this.showError('upload.ErrorVideos', 'snackbar.retryAction');
              return EMPTY;
            }),
          )
          .subscribe(() => {
            this.router.navigate(['/end'], {
              queryParamsHandling: 'preserve',
            });
          });
      });
  }

  uploadTrackingData(url: string) {
    const headers = {
      'x-ms-blob-type': 'BlockBlob',
      'Content-Type': 'application/x-zip',
    };
    return this.http
      .put(url, this.mlTrackingService.trackingDataUpload.blob, { headers })
      .pipe(
        catchError(() => {
          this.showError('upload.ErrorTrackingData', 'snackbar.retryAction');
          return EMPTY;
        }),
      )
      .subscribe();
  }

  uploadDebugData(url: string) {
    const headers = {
      'x-ms-blob-type': 'BlockBlob',
      'Content-Type': 'application/json',
    };
    return this.http
      .put(url, this.debugDataService.debugDataUpload.blob, { headers })
      .pipe(
        catchError(() => {
          this.showError('upload.ErrorDebugData', 'snackbar.retryAction');
          return EMPTY;
        }),
      )
      .subscribe();
  }

  createId(length: number): string {
    const characters =
      'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    return Array(length)
      .fill('')
      .map(() =>
        characters.charAt(Math.floor(Math.random() * characters.length)),
      )
      .join('');
  }

  showError(messageKey: string, actionKey: string) {
    this.snackbarService
      .showSnackBar({
        messageKey,
        actionKey,
      })
      .subscribe(() => {
        this.submitData();
      });
  }
}
